/**
 * Add sale configuration to NFT metadata
 * @param {Object} metadata - Base metadata object
 * @param {Object} saleConfig - Sale configuration from SimpleMintModal
 * @returns {Object} - Metadata with sale attributes
 */
export function addSaleMetadata(metadata, saleConfig) {
  if (!saleConfig || !saleConfig.enabled) {
    return metadata;
  }

  const saleAttributes = [];

  // Add sale price
  if (saleConfig.price !== undefined) {
    saleAttributes.push({
      trait_type: 'Sale Price',
      value: saleConfig.price,
      display_type: 'number',
    });
    saleAttributes.push({
      trait_type: 'Sale Currency',
      value: saleConfig.currency || 'ETH',
    });
  }

  // Add edition info
  if (saleConfig.editionSize !== null && saleConfig.editionSize !== undefined) {
    saleAttributes.push({
      trait_type: 'Edition Size',
      value: saleConfig.editionSize,
      display_type: 'number',
    });
  } else {
    saleAttributes.push({
      trait_type: 'Edition Type',
      value: 'Open Edition',
    });
  }

  // Add end date if set
  if (saleConfig.endDate) {
    const endTimestamp = saleConfig.endDate instanceof Date
      ? Math.floor(saleConfig.endDate.getTime() / 1000)
      : Math.floor(new Date(saleConfig.endDate).getTime() / 1000);
    
    saleAttributes.push({
      trait_type: 'Sale End Date',
      value: endTimestamp,
      display_type: 'date',
    });
  }

  return {
    ...metadata,
    attributes: [...(metadata.attributes || []), ...saleAttributes],
    properties: {
      ...(metadata.properties || {}),
      sale: {
        enabled: true,
        price: saleConfig.price,
        currency: saleConfig.currency || 'ETH',
        editionSize: saleConfig.editionSize,
        endDate: saleConfig.endDate ? new Date(saleConfig.endDate).toISOString() : null,
      },
    },
  };
}

/**
 * Add collection metadata to group NFTs
 * @param {Object} metadata - Base metadata object
 * @param {Object} collectionConfig - Collection configuration
 * @returns {Object} - Metadata with collection attributes
 */
export function addCollectionMetadata(metadata, collectionConfig) {
  if (!collectionConfig || !collectionConfig.enabled || !collectionConfig.name) {
    return metadata;
  }

  return {
    ...metadata,
    attributes: [
      ...(metadata.attributes || []),
      {
        trait_type: 'Collection',
        value: collectionConfig.name,
      },
    ],
    properties: {
      ...(metadata.properties || {}),
      collection: collectionConfig.name,
      collectionDescription: collectionConfig.description || '',
    },
  };
}

/**
 * Add edition number for multi-edition mints
 * @param {Object} metadata - Base metadata object
 * @param {number} editionNumber - Current edition number (1-based)
 * @param {number|null} totalEditions - Total editions (null for open)
 * @returns {Object} - Metadata with edition info
 */
export function addEditionMetadata(metadata, editionNumber, totalEditions) {
  const editionAttr = totalEditions
    ? { trait_type: 'Edition', value: `${editionNumber} of ${totalEditions}` }
    : { trait_type: 'Edition', value: `#${editionNumber}` };

  return {
    ...metadata,
    attributes: [...(metadata.attributes || []), editionAttr],
    properties: {
      ...(metadata.properties || {}),
      editionNumber,
      totalEditions: totalEditions || 'open',
    },
  };
}

export function buildListingMetadata(listingId, productPayload = {}, context = {}) {
  if (!listingId || typeof listingId !== "string") {
    throw new Error("listingId must be a non-empty string");
  }

  // DEBUG: Trace input data
  console.group("[MetadataBuilder] Input Data");
  console.log("listingId:", listingId);
  console.log("productPayload:", {
    hasImages: Array.isArray(productPayload.images),
    imagesCount: productPayload.images?.length || 0,
    images: productPayload.images,
    mediaUrl: productPayload.mediaUrl,
    hasImagePreviews: Array.isArray(productPayload.imagePreviews),
    imagePreviewsCount: productPayload.imagePreviews?.length || 0,
    type: productPayload.type,
    itemName: productPayload.itemName,
  });
  console.groupEnd();

  const payload = productPayload || {};
  const images = Array.isArray(payload.images)
    ? payload.images.filter((url) => typeof url === "string" && url.length > 0)
    : [];

  const fallbackImages = [];
  if (typeof payload.mediaUrl === "string" && payload.mediaUrl.length > 0) {
    fallbackImages.push(payload.mediaUrl);
  }
  if (Array.isArray(payload.imagePreviews)) {
    fallbackImages.push(
      ...payload.imagePreviews.filter(
        (url) => typeof url === "string" && url.length > 0
      )
    );
  }

  const primaryImage = images[0] || fallbackImages[0] || "";

  // Collect additional images for multi-image posts (beyond the primary)
  const allImages = [...new Set([...images, ...fallbackImages])];
  const additionalImages = allImages.length > 1 ? allImages.slice(1) : [];

  // DEBUG: Trace image resolution
  console.group("[MetadataBuilder] Image Resolution");
  console.log("Filtered images:", images);
  console.log("Fallback images:", fallbackImages);
  console.log("Primary image selected:", primaryImage || "NONE");
  console.groupEnd();

  const resolveValue = (...candidates) => {
    for (const candidate of candidates) {
      if (
        candidate !== undefined &&
        candidate !== null &&
        `${candidate}`.trim().length > 0
      ) {
        return candidate;
      }
    }
    return "";
  };

  const sanitizeListingPath = (path) => {
    if (typeof path !== "string") return "";
    if (path.startsWith("itemsTimeline/")) return "";
    return path;
  };

  const category = resolveValue(
    payload.category,
    payload.selectedCategory,
    payload.categoryName
  );
  const condition = resolveValue(
    payload.condition,
    payload.selectedCondition,
    payload.state
  );
  const priceValue = resolveValue(
    payload.price,
    payload.priceInCents,
    payload.price_cents
  );
  const coinSymbol = resolveValue(
    payload.coinSymbol,
    payload.coin?.symbol,
    payload.currencySymbol,
    "UNIT"
  );

  const mediaType = resolveValue(
    context.mediaType,
    payload.mediaType,
    payload.type && `${payload.type}`.startsWith("media")
      ? `${payload.type}`.replace("media_", "")
      : ""
  ).toLowerCase();

  const collectionName = resolveValue(
    context.collectionName,
    payload.collection,
    payload.sourceCollection
  );

  const listingPath = resolveValue(
    sanitizeListingPath(context.listingPath),
    sanitizeListingPath(payload.listingPath),
    sanitizeListingPath(payload._originalPath),
    `products/${listingId}`
  );

  const animationUrl = resolveValue(
    context.animationUrl,
    payload.animation_url,
    payload.animationUrl
  );

  const attributes = [
    category && { trait_type: "Category", value: category },
    condition && { trait_type: "Condition", value: condition },
    priceValue !== "" && {
      trait_type: "Price",
      value: String(priceValue),
    },
    mediaType && {
      trait_type: "Media Type",
      value: mediaType,
    },
  ].filter(Boolean);

  const organisationUid = resolveValue(
    context.organisationUid,
    payload.organisation,
    payload.organisationUid,
    payload.orgUid
  );

  const creatorUid = resolveValue(
    context.creatorUid,
    payload.owner,
    payload.ownerUid,
    payload.creator
  );

  const royaltyBps = Number.isFinite(context.royaltyBps)
    ? Number(context.royaltyBps)
    : undefined;

  // Build external_url pointing to the item's overlay on Unit
  const externalUrl = listingId
    ? `${typeof window !== "undefined" ? window.location.origin : "https://unit.network"}/?item=${listingId}`
    : undefined;

  return {
    name: resolveValue(payload.itemName, payload.title, "Unit Marketplace Item"),
    description: resolveValue(
      payload.itemDescription,
      payload.description,
      payload.summary
    ),
    image: primaryImage,
    ...(externalUrl ? { external_url: externalUrl } : {}),
    attributes,
    properties: {
      listingPath,
      organisationUid: organisationUid || undefined,
      creatorUid: creatorUid || undefined,
      variants: payload.variants || {},
      coinName: resolveValue(payload.coinName, payload.coin?.name) || undefined,
      coinSymbol: coinSymbol || undefined,
      mediaType: mediaType || undefined,
      collection: collectionName || undefined,
      ...(additionalImages.length > 0 ? { additional_images: additionalImages } : {}),
      ...(royaltyBps !== undefined ? { royaltyBps } : {}),
    },
    ...(royaltyBps !== undefined ? { royaltyBps } : {}),
    ...(animationUrl &&
    mediaType &&
    ["video", "audio"].includes(mediaType)
      ? { animation_url: animationUrl }
      : {}),
  };
}
